// theme/theme.tsx — L2 dark-theme switcher.
// Mirrors the i18n provider pattern: Context + Provider + hook.
// Choice lives on <html class="theme-*"> so CSS variables update
// instantly and React isn't asked to re-render the world.

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

export type Theme = 'operator' | 'midnight' | 'ember';

export const ALL_THEMES: Theme[] = ['operator', 'midnight', 'ember'];

export const DEFAULT_THEME: Theme = 'operator';

const STORAGE_KEY = 'theme';

function isValidTheme(s: string | null): s is Theme {
  return s === 'operator' || s === 'midnight' || s === 'ember';
}

export function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isValidTheme(saved) ? saved : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

// Apply theme class to <html>. Called by the inline boot script in
// index.html (before first paint) and by the provider on changes.
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const t of ALL_THEMES) root.classList.remove(`theme-${t}`);
  root.classList.add(`theme-${theme}`);
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: DEFAULT_THEME,
  setTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Read the class set by the inline boot script so the first React
  // render agrees with what's already on the screen. Fall back to
  // localStorage if for some reason the class is missing.
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof document !== 'undefined') {
      for (const t of ALL_THEMES) {
        if (document.documentElement.classList.contains(`theme-${t}`)) return t;
      }
    }
    return readStoredTheme();
  });

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    applyTheme(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* ignore */ }
  }, []);

  // Safety net: if external code cleared the class, re-apply it.
  useEffect(() => {
    if (!document.documentElement.classList.contains(`theme-${theme}`)) {
      applyTheme(theme);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
